import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, Loader2, ChevronRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function KYC() {
  const navigate = useNavigate();
  const { setMember } = useAuth();
  const [idType, setIdType] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [idDoc, setIdDoc] = useState(null);
  const [addrDoc, setAddrDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const uploadFile = async (file, setter, key) => {
    const fd = new FormData();
    fd.append("file", file);
    setter({ uploading: true, name: file.name });
    try {
      const res = await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter({ uploading: false, id: res.data.id, name: file.name });
      toast.success(`${key} uploaded.`);
    } catch (err) {
      setter(null);
      toast.error("Upload failed. Try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!idType || !idNumber || !idDoc?.id || !addrDoc?.id) {
      toast.error("Complete all fields and upload both documents.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/kyc", {
        id_type: idType,
        id_number: idNumber,
        id_document_id: idDoc.id,
        address_document_id: addrDoc.id,
      });
      setMember(res.data);
      toast.success("KYC submitted for review.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <p className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-emerald-400/80 mb-3">
        Step 02 ◆ Identity & KYC
      </p>
      <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-slate-50 leading-tight">
        Verify your identity
      </h1>
      <p className="text-sm text-slate-400 mt-3 leading-relaxed">
        Provide your identity details and upload documents from your camera or gallery. All files are stored securely
        and reviewed under our compliance-first protocol.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Identity document type</label>
          <Select value={idType} onValueChange={setIdType}>
            <SelectTrigger data-testid="kyc-idtype-select" className="hairline-input w-full rounded-lg px-4 py-3 text-sm h-auto">
              <SelectValue placeholder="Select document" />
            </SelectTrigger>
            <SelectContent className="bg-[#0D131C] border-slate-800 text-slate-200">
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="national_id">National ID</SelectItem>
              <SelectItem value="drivers_license">Driver's License</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Document number</label>
          <input
            data-testid="kyc-idnumber-input"
            className="hairline-input w-full rounded-lg px-4 py-3 text-sm"
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            placeholder="Document / ID number"
          />
        </div>

        <UploadTile
          label="Passport / National ID"
          testId="kyc-passport-upload-input"
          state={idDoc}
          onFile={(f) => uploadFile(f, setIdDoc, "Identity document")}
        />
        <UploadTile
          label="Proof of Address"
          testId="kyc-address-upload-input"
          state={addrDoc}
          onFile={(f) => uploadFile(f, setAddrDoc, "Proof of address")}
        />

        <button
          type="submit"
          disabled={submitting}
          data-testid="kyc-submit-button"
          className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-slate-950 font-medium rounded-xl py-4 hover:bg-emerald-400 transition-colors active:scale-[0.99] disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit for Verification"}
          {!submitting && <ChevronRight className="w-4 h-4" />}
        </button>
      </form>
    </Layout>
  );
}

const UploadTile = ({ label, testId, state, onFile }) => {
  const done = state?.id;
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">{label}</label>
      <label
        className={`flex items-center gap-3 rounded-lg border border-dashed p-4 cursor-pointer transition-colors ${
          done ? "border-emerald-600/50 bg-emerald-950/20" : "border-slate-700 bg-[#0D131C]/60 hover:border-slate-600"
        }`}
      >
        <input
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          className="hidden"
          data-testid={testId}
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
        />
        {state?.uploading ? (
          <Loader2 className="w-5 h-5 text-amber-400 animate-spin shrink-0" />
        ) : done ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
        ) : (
          <UploadCloud className="w-5 h-5 text-slate-500 shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-sm text-slate-200 truncate">
            {state?.name || "Tap to capture or upload"}
          </p>
          <p className="font-mono-ui text-[9px] uppercase tracking-[0.15em] text-slate-500 mt-0.5">
            {done ? "Uploaded" : state?.uploading ? "Uploading…" : "JPG · PNG · PDF"}
          </p>
        </div>
      </label>
    </div>
  );
};
